<script lang="ts" setup>
import { UBadge, UButton, UTooltip } from '#components'

const props = defineProps({
  alignment: {
    default: 'left',
    type: String as PropType<BaseBadgesAlignment>,
  },
  badges: {
    required: true,
    type: Array as PropType<(UBadge & { active?: boolean })[]>,
  },
  blockedOperator: {
    default: false,
    type: Boolean,
  },
  clickable: {
    default: false,
    type: Boolean,
  },
  color: {
    default: 'indigo',
    type: String as PropType<UBadgeColor>,
  },
  disabled: {
    default: false,
    type: Boolean,
  },
  max: {
    default: Number.POSITIVE_INFINITY,
    type: Number,
  },
  operator: {
    default: '',
    type: String as PropType<BaseBadgesOperator>,
  },
  removable: {
    default: false,
    type: Boolean,
  },
  size: {
    default: 'sm',
    type: String as PropType<UBadgeSize | UButtonSize>,
  },
  uppercase: {
    default: true,
    type: Boolean,
  },
  variant: {
    default: 'soft',
    type: String as PropType<UButtonVariant>,
  },
  wrap: {
    default: true,
    type: Boolean,
  },
})

const emit = defineEmits<{
  badgeClick: [badge: UBadge, badgeIndex: number]
  badgeUpdated: [badge: UBadge]
  operatorClick: [badge: UBadge, badgeIndex: number]
}>()

const { t } = useI18n()

const menusOpened = ref<string[]>([])
const selectedBadge = ref<string>()
const showAllBadges = ref(false)

const cappedBadges = computed(() => {
  return props.badges.filter((_badge, badgeIndex) => {
    return badgeIndex < props.max || showAllBadges.value
  })
})

const isClickable = computed(() => {
  return props.clickable || props.removable
})

const reverseOperator = computed(() => {
  return props.operator === 'and' ? 'or' : 'and'
})

function onShowAllBadges() {
  showAllBadges.value = true
}

function onBadgeClick(badge: UBadge, badgeIndex: number) {
  emit('badgeClick', badge, badgeIndex)
}

function onBadgeSelected(badgeValue: string | undefined) {
  selectedBadge.value = badgeValue
}

function onBadgeOperatorClicked(badge: UBadge, badgeIndex: number) {
  emit('operatorClick', badge, badgeIndex)
}

function onToggleMenu(badge: UBadge) {
  if (badge.menu?.id) {
    if (menusOpened.value.includes(badge.menu.id)) {
      menusOpened.value = menusOpened.value.filter(id => id !== badge.menu!.id)
      badge.menu.value = ''

      emit('badgeUpdated', badge)
    }
    else {
      menusOpened.value.push(badge.menu.id)
    }
  }
  else {
    console.warn('Badge has no menu defined')
  }
}
</script>

<template>
  <div
    class="flex items-center"
    :class="{
      'flex-wrap': wrap,
      'justify-center': alignment === 'center',
      'justify-end': alignment === 'right',
      'justify-start': alignment === 'left',
      'gap-2': ['2xs', 'xs', 'sm'].includes(size as string),
      'gap-2.5': ['md', 'lg'].includes(size as string),
      'pointer-events-none': disabled,
    }"
  >
    <template
      v-for="(badge, badgeIndex) in cappedBadges"
      :key="badge.value || badgeIndex"
    >
      <template v-if="badge">
        <Component
          :is="badge.tooltip ? UTooltip : 'span'"
          :text="badge.tooltip"
          :ui="{ width: 'max-w-lg' }"
        >
          <div class="flex items-center font-mono">
            <Component
              :is="isClickable ? UButton : UBadge"
              class="font-mono"
              :class="{ 'rounded-r-none': !!badge.menu, 'ring-1 ring-indigo-600': badge.active }"
              :color="badge.color || color"
              :loading="badge.loading"
              :size="size"
              :to="badge.to"
              :variant="badge.variant || variant"
              @blur="onBadgeSelected(undefined)"
              @click.stop="onBadgeClick(badge, badgeIndex)"
              @focus="onBadgeSelected(badge.value)"
              @mouseenter="onBadgeSelected(badge.value)"
              @mouseleave="onBadgeSelected(undefined)"
            >
              <div class="flex items-center gap-1.5">
                <UIcon
                  v-if="removable && selectedBadge === badge.value"
                  class="size-4"
                  :name="getIcon('close')"
                />

                <UAvatar
                  v-else-if="badge.avatar"
                  :alt="badge.label"
                  class="rounded"
                  img-class="rounded"
                  size="3xs"
                  :src="badge?.avatar"
                />

                <BaseCircle
                  v-else-if="badge.circle"
                  class="mx-0.5 size-3"
                  :color="badge.circle"
                />

                <UIcon
                  v-else-if="badge.flag"
                  class="size-4"
                  :name="`flagpack:${badge.flag}`"
                />

                <UIcon
                  v-else-if="badge.active"
                  class="size-4"
                  :name="getIcon('check')"
                />

                <UIcon
                  v-else-if="badge.icon"
                  class="size-4"
                  :name="badge.icon"
                />

                <div
                  class="text-xs"
                  :class="{ uppercase }"
                >
                  {{ badge.label }}
                </div>
              </div>
            </Component>

            <template v-if="badge.menu">
              <UButton
                v-if="menusOpened.includes(badge.menu.id) || badge.menu.value"
                class="-ml-px h-[32px] rounded-none px-2"
                :color="badge.color || color"
                :size="size"
                :variant="variant"
              >
                <slot
                  :badge="badge"
                  name="badge-menu"
                />
              </UButton>

              <UTooltip
                :prevent="!badge.menu.tooltip"
                :text="badge.menu.tooltip"
              >
                <UButton
                  class="-ml-px h-[32px] rounded-l-none px-2"
                  :color="badge.color || color"
                  :size="size"
                  :variant="variant"
                  @click="onToggleMenu(badge)"
                >
                  <UIcon
                    class="size-3"
                    :name="getIcon(menusOpened.includes(badge.menu.id) || badge.menu.value ? 'add' : 'caret')"
                  />
                </UButton>
              </UTooltip>
            </template>
          </div>
        </Component>

        <Component
          :is="!blockedOperator ? UTooltip : 'span'"
          v-if="operator && cappedBadges.length > 1 && badgeIndex !== cappedBadges.length - 1"
          class="flex w-5 items-center justify-center normal-case text-gray-900 hover:text-gray-600 dark:text-gray-100 dark:hover:text-gray-400"
          :text="!blockedOperator ? t('switch', { operator: (t(`operators.${reverseOperator}`)).toUpperCase() }) : undefined"
        >
          <Component
            :is="isClickable ? UButton : UBadge"
            class="uppercase text-gray-900 hover:text-gray-600 focus-visible:text-gray-600 focus-visible:underline focus-visible:ring-0 dark:text-gray-100 dark:hover:text-gray-400 dark:focus-visible:text-gray-400"
            :class="{ 'pointer-events-none': blockedOperator }"
            :label="t(`operators.${operator}`)"
            :size="size"
            :variant="clickable ? 'link' : 'ghost'"
            @click="onBadgeOperatorClicked(badge, badgeIndex)"
          />
        </Component>
      </template>
    </template>

    <UTooltip
      v-if="badges.length > max && !showAllBadges"
      :text="t('globals.showAll')"
    >
      <Component
        :is="isClickable ? UButton : UBadge"
        class="cursor-pointer"
        :size="size"
        variant="subtle"
        @click="onShowAllBadges"
      >
        <UIcon :name="getIcon('chevronDown')" />

        {{ t('showMore', { count: badges.length - max }) }}
      </Component>
    </UTooltip>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "operators": {
      "and": "and",
      "or": "or"
    },
    "showMore": "Show {count} more",
    "switch": "Switch to {operator}"
  },
  "es": {
    "operators": {
      "and": "y",
      "or": "o"
    },
    "showMore": "Mostrar {count} más",
    "switch": "Cambiar a {operator}"
  },
  "fr": {
    "operators": {
      "and": "et",
      "or": "ou"
    },
    "showMore": "Afficher {count} autres",
    "switch": "Basculer à {operator}"
  }
}
</i18n>
